import { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import { AuthContext } from "../../../context/AuthContext";
import {
  BACKGROUND_COLORS,
  BORDER_COLORS,
  TEXT_COLORS,
} from "../../../constants/design/colors";
import { affiliateError } from "../../../hooks/api/admin/useAffiliates";
import {
  MakerObservability,
  MakerObservation,
  formatPoints,
  needsAttention,
} from "./types";
import { useMakerObservability } from "./useMakerObservability";

const Surface = styled.div`
  color: ${TEXT_COLORS.one};
  .monitor-heading,
  .monitor-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
  }
  h2 {
    margin: 0 0 8px;
    font-size: 22px;
  }
  h3 {
    margin: 0 0 12px;
    font-size: 16px;
  }
  p {
    color: ${TEXT_COLORS.two};
    line-height: 1.5;
    margin: 8px 0;
  }
  button,
  input,
  select {
    background: ${BACKGROUND_COLORS.eight};
    color: inherit;
    border: 1px solid ${BORDER_COLORS.two};
    border-radius: 6px;
    font: inherit;
    padding: 10px 12px;
  }
  button {
    cursor: pointer;
  }
  button:disabled {
    opacity: 0.45;
    cursor: default;
  }
  label {
    display: flex;
    flex-direction: column;
    gap: 6px;
    font-size: 13px;
  }
  .monitor-toolbar {
    margin: 16px 0;
    align-items: end;
  }
  .monitor-search {
    flex: 1;
    min-width: 180px;
    max-width: 550px;
  }
  .monitor-cards {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
    margin: 16px 0;
  }
  .monitor-card {
    padding: 14px 16px;
    background: ${BACKGROUND_COLORS.component};
    border: 1px solid ${BORDER_COLORS.one};
    border-radius: 10px;
  }
  .monitor-number {
    display: block;
    font-size: 24px;
    margin-top: 10px;
    font-variant-numeric: tabular-nums;
  }
  .monitor-banner {
    padding: 16px;
    border: 1px solid #68512f;
    border-radius: 8px;
    background: #211c14;
    margin: 16px 0;
  }
  .monitor-banner p {
    color: #ecd6ad;
  }
  .monitor-muted {
    color: ${TEXT_COLORS.two};
    font-size: 13px;
  }
  .monitor-table {
    overflow-x: auto;
  }
  table[aria-label="Maker performance"] th:first-child,
  table[aria-label="Maker performance"] td:first-child {
    min-width: 200px;
  }
  .monitor-mobile-hint {
    display: none;
  }
  table {
    width: 100%;
    border-collapse: collapse;
  }
  th,
  td {
    text-align: left;
    padding: 14px 12px;
    border-bottom: 1px solid ${BORDER_COLORS.one};
    vertical-align: top;
  }
  th {
    font-weight: 400;
    color: ${TEXT_COLORS.two};
    font-size: 12px;
    white-space: nowrap;
  }
  .monitor-detail {
    padding: 20px;
    background: #101019;
    border-radius: 8px;
  }
  .monitor-detail td,
  .monitor-detail th {
    padding: 10px;
  }
  .monitor-detail ul {
    margin: 8px 0 16px;
    padding-left: 20px;
  }
  .monitor-detail li {
    margin: 6px 0;
  }
  .monitor-wallet {
    overflow-wrap: anywhere;
    font-size: 12px;
    color: ${TEXT_COLORS.two};
    display: block;
    margin: 6px 0;
    max-width: 330px;
  }
  .monitor-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border-radius: 20px;
    padding: 5px 9px;
    white-space: nowrap;
    font-size: 12px;
    background: #24242c;
  }
  .monitor-badge[data-tone="good"] {
    color: #8ce9bc;
    background: #122a22;
  }
  .monitor-badge[data-tone="bad"] {
    color: #ffadb9;
    background: #331d25;
  }
  .monitor-badge[data-tone="warn"] {
    color: #f1ce8d;
    background: #30291b;
  }
  .monitor-period {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    margin: 12px 0;
  }
  .monitor-period > span {
    color: ${TEXT_COLORS.two};
    font-size: 13px;
  }
  .monitor-diagnostics {
    margin-top: 20px;
    border-top: 1px solid ${BORDER_COLORS.one};
    padding-top: 16px;
  }
  summary {
    cursor: pointer;
    color: ${TEXT_COLORS.two};
    font-size: 13px;
  }
  .monitor-alert-summary {
    margin: 12px 0;
    font-size: 13px;
    color: ${TEXT_COLORS.two};
  }
  .monitor-policy {
    margin: 16px 0;
    padding: 16px;
    border: 1px solid ${BORDER_COLORS.one};
    border-radius: 8px;
    overflow-wrap: anywhere;
  }
  @media (max-width: 760px) {
    .monitor-mobile-hint {
      display: block;
    }
    .monitor-cards {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
    .monitor-card {
      padding: 12px;
    }
    .monitor-number {
      font-size: 23px;
    }
    .monitor-detail {
      padding: 10px;
    }
  }
`;
const reasonCopy: Record<string, string> = {
  missing_check_windows:
    "Some scheduled check windows are missing. Ask engineering to inspect the collector before judging this hour.",
  incomplete_capture: "This hour has incomplete collection evidence.",
  volatility_baseline_unavailable:
    "Warming up: the seven-day volatility baseline is unavailable. Volatility assessment is not ready.",
  before_collection_policy:
    "This hour is before the collection policy became effective.",
  collection_not_configured: "Finalized collection is not configured.",
  history_unavailable:
    "History collection could not be read. Ask engineering to inspect the History service and storage.",
  scoring_evidence_unavailable:
    "Scoring evidence is unavailable; this is not a maker failure.",
  agreement_changed_during_hour:
    "The maker agreement changed during this hour. A complete assessment is unavailable.",
  partial_hour_membership: "The maker was enrolled for only part of this hour.",
  commitment_evidence_unavailable:
    "Commitment evidence is unavailable; do not treat missing evidence as failed quoting.",
  market_evidence_unavailable: "Market evidence is unavailable for this hour.",
  missing_checks:
    "Some scheduled checks are missing. Check collector health before judging this maker.",
  no_checks: "No checks were recorded for this hour.",
  collection_unavailable:
    "Collection data is unavailable; maker performance cannot be assessed.",
  reference_unavailable: "Market reference data is unavailable.",
  stale_reference: "Market reference data was too old to score safely.",
  hour_boundary_not_observed:
    "Waiting for a complete hourly options universe. This is a data gap, not a maker failure.",
  volatility_warming_up:
    "The seven-day volatility baseline is still warming up.",
  policy_not_configured: "The finalized scoring policy is not configured.",
  membership_changed:
    "Membership changed during the hour; this is not a complete hour of the current agreement.",
  not_enrolled_during_hour:
    "This maker was not enrolled during the selected hour.",
};
export const reasonText = (code: string) =>
  reasonCopy[code] || code.replace(/_/g, " ");
export const utc = (value: string | null) =>
  value
    ? `${new Date(value)
        .toISOString()
        .replace("T", " ")
        .replace(/\.\d{3}Z$/, " UTC")}`
    : "Not available";
const percent = (passed: number, total: number) =>
  total > 0 ? `${((passed / total) * 100).toFixed(1)}%` : "Unavailable";
function Badge({
  children,
  tone = "neutral",
}: {
  children: React.ReactNode;
  tone?: string;
}) {
  return (
    <span className="monitor-badge" data-tone={tone}>
      {children}
    </span>
  );
}
function makerLabel(m: MakerObservation) {
  if (!m.enabled) return { label: "Disabled", tone: "neutral" };
  if (m.status === "not_enrolled_during_hour")
    return { label: "Not enrolled this hour", tone: "neutral" };
  if (m.status === "unavailable" || m.status === "not_observed")
    return { label: "Data unavailable", tone: "warn" };
  if (m.status === "partial" || m.missing_checks > 0)
    return { label: "Incomplete data", tone: "warn" };
  if (m.commitments.some((c) => c.assessment === "below"))
    return { label: "Below commitment", tone: "bad" };
  if (
    m.reasons.length > 0 ||
    m.commitments.some(
      (c) => c.assessment === "unavailable" || c.gap_checks > 0
    ) ||
    !m.commitments.length
  )
    return { label: "Assessment unavailable", tone: "warn" };
  if (m.commitments.every((c) => c.assessment === "outside_agreed_hours"))
    return { label: "Outside agreed hours", tone: "neutral" };
  return { label: "Hour meeting", tone: "good" };
}
function MakerDetail({
  maker,
  uncertain,
}: {
  maker: MakerObservation;
  uncertain: boolean;
}) {
  return (
    <div className="monitor-detail">
      <h3>{maker.maker_id} · Hour detail</h3>
      <p>
        Current membership version {maker.membership_version}.{" "}
        {maker.observed_checks} of {maker.expected_checks} expected checks
        recorded; {maker.missing_checks} missing.
      </p>
      {!!maker.reasons.length && (
        <ul>
          {maker.reasons.map((r, i) => (
            <li key={`${r}:${i}`}>{reasonText(r)}</li>
          ))}
        </ul>
      )}
      <h3>Quoting commitments</h3>
      {uncertain && (
        <p>
          <Badge tone="warn">
            Assessment provisional — incomplete or stale evidence
          </Badge>
        </p>
      )}
      <p>
        Each row is an hourly commitment check. Options require every eligible
        expiry slot. “Hour meeting” does not confirm weekly eligibility or trial
        completion.
      </p>
      {!maker.commitments.length ? (
        <p>No commitment assessment available for this hour.</p>
      ) : (
        <div className="monitor-table">
          <table aria-label={`${maker.maker_id} commitments`}>
            <thead>
              <tr>
                <th>Market / expiry slot</th>
                <th>Hour assessment</th>
                <th>Passed / valid</th>
                <th>Presence</th>
                <th>Data gaps</th>
                <th>Volatile passed / valid</th>
              </tr>
            </thead>
            <tbody>
              {maker.commitments.map((c, i) => (
                <tr key={`${c.market}:${c.slot}:${i}`}>
                  <td>
                    {c.market}
                    {c.slot !== null && (
                      <span className="monitor-muted"> · Slot {c.slot}</span>
                    )}
                  </td>
                  <td>
                    <Badge
                      tone={
                        uncertain
                          ? "warn"
                          : {
                              below: "bad",
                              meeting: "good",
                              unavailable: "warn",
                              outside_agreed_hours: "neutral",
                            }[c.assessment]
                      }
                    >
                      {uncertain && "Unverified: "}
                      {
                        {
                          meeting: "Hour meeting",
                          below: "Below commitment",
                          unavailable: "Unavailable",
                          outside_agreed_hours: "Outside agreed hours",
                        }[c.assessment]
                      }
                    </Badge>
                  </td>
                  <td>
                    {c.passed_checks} / {c.valid_checks}
                    <span className="monitor-wallet">
                      {c.expected_checks} expected
                    </span>
                  </td>
                  <td>{percent(c.passed_checks, c.valid_checks)}</td>
                  <td>{c.gap_checks}</td>
                  <td>
                    {c.passed_volatile_checks} / {c.volatile_checks}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <h3 style={{ marginTop: 24 }}>Recorded points by market</h3>
      <p>
        Observed quote points only; these are not AEVO amounts or approved
        payouts. Missing data may reduce this total.
      </p>
      {!maker.markets.length ? (
        <p>No market score available.</p>
      ) : (
        <div className="monitor-table">
          <table aria-label={`${maker.maker_id} market scores`}>
            <thead>
              <tr>
                <th>Market</th>
                <th>Product</th>
                <th>Observed points</th>
                <th>Valid quote checks</th>
                <th>Data gaps</th>
              </tr>
            </thead>
            <tbody>
              {maker.markets.map((market, i) => (
                <tr key={`${market.market}:${i}`}>
                  <td>{market.market}</td>
                  <td>{market.product}</td>
                  <td title={`Exact: ${market.points}`}>
                    {formatPoints(market.points)}
                  </td>
                  <td>{market.valid_checks}</td>
                  <td>{market.gap_checks}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <h3 style={{ marginTop: 24 }}>Family wallets</h3>
      {maker.wallets.map((w) => (
        <span className="monitor-wallet" key={w}>
          {w}
        </span>
      ))}
    </div>
  );
}
export function MakerMonitoringView({
  data,
  error,
  busy = false,
  onRefresh,
  hour = "",
  onHourChange,
  now = Date.now(),
}: {
  data?: MakerObservability;
  error?: unknown;
  busy?: boolean;
  onRefresh: () => void;
  hour?: string;
  onHourChange?: (h: string) => void;
  now?: number;
}) {
  const [search, setSearch] = useState("");
  const [attention, setAttention] = useState(false);
  const [expanded, setExpanded] = useState<string>();
  const [chosenHour, setChosenHour] = useState("");
  const [hourError, setHourError] = useState("");
  const stale = !!data && now - Date.parse(data.collector.checked_at) > 120000;
  const invalidCurrent = !!error || stale;
  const uncertainHour =
    !!data &&
    (data.status !== "observed" ||
      data.missing_checks > 0 ||
      data.gap_checks > 0 ||
      data.reasons.length > 0);
  const collectorTooOld =
    !!data &&
    [data.collector.latest_check_at, data.collector.latest_reference_at].some(
      (at) =>
        !at || now - Date.parse(at) > data.collector.stale_after_seconds * 1000
    );
  const currentUncertain =
    invalidCurrent ||
    collectorTooOld ||
    !data?.current.at ||
    data.current.status !== "observed" ||
    data.current.reasons.length > 0 ||
    data.collector.status !== "fresh" ||
    now - Date.parse(data.current.at) >
      data.collector.stale_after_seconds * 1000;
  const currentByID = new Map(
    data?.current.makers.map((m) => [m.maker_id, m]) || []
  );
  let collectorLabel = "Unavailable";
  if (data?.collector.status === "fresh" && !collectorTooOld)
    collectorLabel = "Receiving data";
  if (data?.collector.status === "stale" || collectorTooOld)
    collectorLabel = "Stale";
  if (invalidCurrent) collectorLabel = "Unknown";
  const makers = data?.makers || [];
  const active = makers.filter((m) => m.enabled);
  const below = active.filter(
    (m) =>
      m.commitments.some((c) => c.assessment === "below") &&
      m.status === "observed"
  );
  const unknown = active.filter(
    (m) =>
      uncertainHour ||
      ["partial", "unavailable", "not_observed"].includes(m.status) ||
      m.commitments.length === 0 ||
      m.commitments.some((c) => c.assessment === "unavailable")
  );
  const filtered = makers.filter(
    (m) =>
      (!attention ||
        needsAttention(m) ||
        (m.enabled &&
          (currentUncertain ||
            !currentByID.has(m.maker_id) ||
            needsAttention(currentByID.get(m.maker_id)!))) ||
        (m.enabled && (uncertainHour || invalidCurrent))) &&
      [m.maker_id, ...m.wallets].some((v) =>
        v.toLowerCase().includes(search.trim().toLowerCase())
      )
  );
  return (
    <Surface aria-label="Maker performance monitoring">
      <div className="monitor-heading">
        <div>
          <h2>Maker performance</h2>
          <p className="monitor-muted">
            Current quotes and completed-hour performance.
          </p>
        </div>
        <button type="button" onClick={onRefresh} disabled={busy}>
          {busy ? "Refreshing monitoring…" : "Refresh monitoring"}
        </button>
      </div>
      {!data && !error && <p role="status">Loading maker monitoring…</p>}
      {!!error && (
        <div className="monitor-banner" role="alert">
          <strong>Monitoring could not refresh</strong>
          <p>{affiliateError(error)}</p>
          <p>
            {data
              ? "Showing the last verified response. It does not establish current health."
              : "Performance is unknown until data loads. Memberships remain available in their tab."}
          </p>
        </div>
      )}
      {stale && (
        <div className="monitor-banner" role="alert">
          <strong>Monitoring view is stale</strong>
          <p>
            No freshness check in over two minutes. Refresh before relying on
            these results.
          </p>
        </div>
      )}
      {hourError && <p role="alert">{hourError}</p>}
      {!data && hour && onHourChange && (
        <div className="monitor-period">
          <span>Selected completed hour: {utc(hour)}</span>
          <button
            type="button"
            onClick={() => {
              setChosenHour("");
              setHourError("");
              onHourChange("");
            }}
          >
            Latest completed hour
          </button>
        </div>
      )}
      {data && (
        <>
          <div className="monitor-period">
            <span>
              {hour ? "Selected completed hour" : "Latest completed hour"}:{" "}
              {utc(data.start)} → {utc(data.end)}
            </span>
          </div>
          <div className="monitor-cards">
            <div className="monitor-card">
              Enabled memberships
              <strong className="monitor-number">{active.length}</strong>
              <span className="monitor-muted">
                {makers.length} total, including disabled
              </span>
            </div>
            <div className="monitor-card">
              Below commitment
              <strong className="monitor-number">
                {invalidCurrent || uncertainHour ? "—" : below.length}
              </strong>
              <span className="monitor-muted">
                Completed hour, sufficient data
              </span>
            </div>
            <div className="monitor-card">
              Incomplete / unknown
              <strong className="monitor-number">
                {invalidCurrent ? "—" : unknown.length}
              </strong>
              <span className="monitor-muted">
                Inspect collection before judging
              </span>
            </div>
            <div className="monitor-card">
              Collector now
              <strong className="monitor-number" style={{ fontSize: 22 }}>
                {collectorLabel}
              </strong>
              <span className="monitor-muted">
                Freshness limit {data.collector.stale_after_seconds}s
              </span>
            </div>
          </div>
          {(uncertainHour ||
            currentUncertain ||
            below.length > 0 ||
            unknown.length > 0) && (
            <p className="monitor-alert-summary">
              {uncertainHour || currentUncertain
                ? "Data needs review. Missing evidence is not a maker failure."
                : "Some makers need attention. Open a maker to inspect the affected markets."}
            </p>
          )}
          <div className="monitor-toolbar">
            <label className="monitor-search" htmlFor="maker-monitor-search">
              Find a maker or wallet
              <input
                id="maker-monitor-search"
                type="search"
                placeholder="Maker ID or wallet address"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </label>
            <label htmlFor="maker-monitor-filter">
              Show
              <select
                id="maker-monitor-filter"
                aria-label="Maker filter"
                value={attention ? "attention" : "all"}
                onChange={(e) => setAttention(e.target.value === "attention")}
              >
                <option value="all">All makers</option>
                <option value="attention">Needs attention</option>
              </select>
            </label>
            <span className="monitor-muted">
              {filtered.length} of {makers.length} makers
            </span>
          </div>
          {!makers.length && (
            <div className="monitor-policy">
              <h3>No maker memberships yet</h3>
              <p>
                Collection health is shown above. There is no maker performance
                to assess. Open Memberships to enroll a maker.
              </p>
            </div>
          )}
          {!!makers.length && !filtered.length && (
            <p>No makers match this search and filter.</p>
          )}
          {!!filtered.length && (
            <div className="monitor-table">
              <p className="monitor-mobile-hint monitor-muted">
                Swipe horizontally to view all columns.
              </p>
              <table aria-label="Maker performance">
                <thead>
                  <tr>
                    <th>Maker / wallet</th>
                    <th>Latest quote check</th>
                    <th>Completed-hour status</th>
                    <th>Checks received</th>
                    <th>Perps points</th>
                    <th>Options points</th>
                    <th>Inspect</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((m) => {
                    const status = makerLabel(m);
                    if (
                      uncertainHour &&
                      m.enabled &&
                      m.status !== "not_enrolled_during_hour"
                    ) {
                      status.label = "Incomplete collection";
                      status.tone = "warn";
                    }
                    return (
                      <MakerRows
                        key={m.maker_id}
                        maker={m}
                        status={status}
                        current={currentByID.get(m.maker_id)}
                        currentAt={data.current.at}
                        currentUncertain={currentUncertain}
                        historical={invalidCurrent}
                        hourUncertain={uncertainHour}
                        expanded={expanded === m.maker_id}
                        onToggle={() =>
                          setExpanded(
                            expanded === m.maker_id ? undefined : m.maker_id
                          )
                        }
                      />
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <details className="monitor-diagnostics">
            <summary>Data health, past hours & calculation policy</summary>
            <p className="monitor-muted">
              Scores calculated {utc(data.computed_at)} · Collector checked{" "}
              {utc(data.collector.checked_at)}.
            </p>
            {onHourChange && (
              <div className="monitor-toolbar">
                <label htmlFor="maker-observation-hour">
                  Observation hour
                  <input
                    id="maker-observation-hour"
                    aria-label="Observation hour"
                    type="datetime-local"
                    step="3600"
                    value={chosenHour}
                    onChange={(e) => setChosenHour(e.target.value)}
                  />
                </label>
                <button
                  type="button"
                  onClick={() => {
                    const date = new Date(`${chosenHour}:00Z`);
                    if (
                      !chosenHour ||
                      !Number.isFinite(date.getTime()) ||
                      date.getTime() % 3600000 ||
                      date.getTime() + 3600000 > now ||
                      now - date.getTime() > 8 * 86400000
                    ) {
                      setHourError(
                        "Choose a completed UTC hour within the past eight days."
                      );
                      return;
                    }
                    setHourError("");
                    onHourChange(date.toISOString());
                  }}
                >
                  View UTC hour
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setChosenHour("");
                    setHourError("");
                    onHourChange("");
                  }}
                  disabled={!hour}
                >
                  Latest completed hour
                </button>
                <span className="monitor-muted">
                  All times UTC · auto-refresh every 10 seconds while visible
                </span>
              </div>
            )}
            <div className="monitor-policy">
              <strong>Collection evidence</strong>
              <p>
                Latest quote check: {utc(data.current.at)}. This is a snapshot;
                the table separately shows the completed-hour assessment.
              </p>
              {!!data.current.reasons.length && (
                <ul>
                  {data.current.reasons.map((r, i) => (
                    <li key={i}>{reasonText(r)}</li>
                  ))}
                </ul>
              )}
              <p>
                {data.observed_checks} / {data.expected_checks} scheduled checks
                recorded · {data.missing_checks} missing · {data.gap_checks}{" "}
                explicitly marked as gaps.
              </p>
              <details>
                <summary>What to check when something is wrong</summary>
                <ul>
                  <li>
                    Missing or stale collection: ask engineering to inspect
                    History collection and market references; do not mark makers
                    as failed.
                  </li>
                  <li>
                    Below commitment with complete data: inspect the maker’s
                    market and expiry rows, then check its quoting sizes,
                    spreads and agreed hours.
                  </li>
                  <li>
                    Warm-up or unavailable references: wait for a valid baseline
                    and recheck; do not interpret gaps as zero performance.
                  </li>
                </ul>
              </details>
              <p>
                Latest check: {utc(data.collector.latest_check_at)}
                <br />
                Latest reference: {utc(data.collector.latest_reference_at)}
              </p>
              {(data.status !== "observed" ||
                data.collector.status !== "fresh" ||
                collectorTooOld) && (
                <p>
                  <Badge tone="warn">
                    {data.status === "not_configured"
                      ? "Policy not configured"
                      : "Data needs attention"}
                  </Badge>{" "}
                  Incomplete or stale data is not proof that a maker failed.
                </p>
              )}
              {!!data.reasons.length && (
                <ul>
                  {data.reasons.map((r, i) => (
                    <li key={`${r}:${i}`}>{reasonText(r)}</li>
                  ))}
                </ul>
              )}
            </div>
            <div className="monitor-policy">
              <details>
                <summary>Active calculation policy and interpretation</summary>
                <p>
                  Policy{" "}
                  <strong>{data.policy_version || "Not configured"}</strong>
                  <br />
                  Collection effective {utc(data.policy_effective_at)}
                  <br />
                  Reward policy effective {utc(data.reward_effective_at)}
                </p>
                <p>
                  This dashboard observes a completed hour. It does not certify
                  weekly Meeting status, trial completion, rebate entitlement or
                  payout approval. Points remain separate from commitment
                  assessments. Weekly settlement requires its own verified
                  record and staff review.
                </p>
              </details>
            </div>
          </details>
        </>
      )}
    </Surface>
  );
}
function MakerRows({
  maker: m,
  status,
  current,
  currentAt,
  currentUncertain,
  hourUncertain,
  historical,
  expanded,
  onToggle,
}: {
  maker: MakerObservation;
  status: { label: string; tone: string };
  current?: MakerObservation;
  currentAt: string | null;
  currentUncertain: boolean;
  hourUncertain: boolean;
  historical: boolean;
  expanded: boolean;
  onToggle: () => void;
}) {
  const available = ![
    "unavailable",
    "not_observed",
    "not_enrolled_during_hour",
  ].includes(m.status);
  const latest = current
    ? makerLabel(current)
    : { label: "Unavailable", tone: "warn" };
  if (latest.label === "Hour meeting") latest.label = "Quoting to commitment";
  return (
    <>
      <tr>
        <td>
          <strong>{m.maker_id}</strong>
          <span className="monitor-wallet">
            {m.wallets[0] || "No wallet in response"}
            {m.wallets.length > 1 && ` +${m.wallets.length - 1} more`}
          </span>
        </td>
        <td>
          <Badge tone={currentUncertain ? "warn" : latest.tone}>
            {currentUncertain ? "Current data unavailable" : latest.label}
          </Badge>
          <span className="monitor-wallet" title={utc(currentAt)}>
            Single quote check
          </span>
        </td>
        <td>
          <Badge tone={historical ? "neutral" : status.tone}>
            {historical ? `Last response: ${status.label}` : status.label}
          </Badge>
        </td>
        <td>
          {m.observed_checks} / {m.expected_checks}
        </td>
        <td title={available ? `Exact: ${m.points.PERPS}` : undefined}>
          {available ? formatPoints(m.points.PERPS) : "Unavailable"}
        </td>
        <td title={available ? `Exact: ${m.points.OPTIONS}` : undefined}>
          {available ? formatPoints(m.points.OPTIONS) : "Unavailable"}
        </td>
        <td>
          <button
            type="button"
            aria-expanded={expanded}
            aria-label={`${expanded ? "Hide" : "Inspect"} ${m.maker_id}`}
            onClick={onToggle}
          >
            {expanded ? "Hide" : "Inspect"}
          </button>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={7}>
            <div className="monitor-detail">
              <h3>Latest quote check · {utc(currentAt)}</h3>
              <p>
                A single completed quote check, not an hourly or weekly
                qualification.{" "}
                {currentUncertain
                  ? "Current evidence is unavailable or stale."
                  : "Inspect current commitments below."}
              </p>
              {current?.commitments.map((c, i) => (
                <p key={i}>
                  {c.market}
                  {c.slot !== null ? ` · Slot ${c.slot}` : ""}:{" "}
                  {currentUncertain
                    ? "Unknown"
                    : {
                        meeting: "Quote meets commitment",
                        below: "Quote below commitment",
                        outside_agreed_hours: "Outside agreed hours",
                        unavailable: "Data unavailable",
                      }[c.assessment]}
                </p>
              ))}
              {!!current?.markets.some((market) => market.latest_quote) && (
                <div className="monitor-table">
                  <table aria-label={`${m.maker_id} latest quote quality`}>
                    <thead>
                      <tr>
                        <th>Market</th>
                        <th>Bid / ask depth (USD)</th>
                        <th>Minimum per side (USD)</th>
                        <th>Bid / ask distance</th>
                        <th>Price band</th>
                      </tr>
                    </thead>
                    <tbody>
                      {current.markets
                        .filter((market) => market.latest_quote)
                        .map((market, i) => {
                          const q = market.latest_quote!;
                          const amount = (v: string | null) =>
                            v === null ? "Unavailable" : formatPoints(v);
                          const distance = (v: string | null) =>
                            v === null
                              ? "Unavailable"
                              : `${v.startsWith("-") ? "−" : ""}${formatPoints(
                                  v.replace(/^-/, "")
                                )}`;
                          return (
                            <tr key={i}>
                              <td>
                                {market.market}
                                {q.reason && (
                                  <span className="monitor-wallet">
                                    {reasonText(q.reason)}
                                  </span>
                                )}
                              </td>
                              <td>
                                {amount(q.bid_depth_usd)} /{" "}
                                {amount(q.ask_depth_usd)}
                              </td>
                              <td>{amount(q.minimum_side_usd)}</td>
                              <td>
                                {distance(q.bid_distance)} /{" "}
                                {distance(q.ask_distance)}{" "}
                                {q.distance_unit === "vol_points"
                                  ? "vol points"
                                  : "bps"}
                              </td>
                              <td>
                                {currentUncertain || market.valid_checks === 0
                                  ? "Unverified"
                                  : `Bid ${
                                      q.bid_within_band
                                        ? "within"
                                        : "outside / missing"
                                    } · Ask ${
                                      q.ask_within_band
                                        ? "within"
                                        : "outside / missing"
                                    }`}
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              )}
              {current?.reasons.map((r, i) => (
                <p key={i}>{reasonText(r)}</p>
              ))}
            </div>
            <MakerDetail
              maker={m}
              uncertain={hourUncertain || historical || m.status !== "observed"}
            />
          </td>
        </tr>
      )}
    </>
  );
}
export function MakerMonitoring() {
  const { tokenId } = useContext(AuthContext);
  const [hour, setHour] = useState("");
  const [now, setNow] = useState(Date.now());
  const read = useMakerObservability(hour);
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(timer);
  }, []);
  if (!tokenId) return null;
  return (
    <MakerMonitoringView
      data={read.data}
      error={read.error}
      busy={read.isValidating}
      onRefresh={() => read.mutate()}
      hour={hour}
      onHourChange={setHour}
      now={now}
    />
  );
}
